// DeepgramService.ts
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk'
import Logger from '@/services/Logger.js'
import type { TranscriptionResult } from '@/customTypes'
import { useDataStore } from '@/stores/DataStore'

export type DeepgramConnectionHandle = {
  connection: any
  questionId: number
  disposed: boolean
}

export class DeepgramService {
  private logger: any
  private lastProcessedIndex: number = 0

  constructor() {
    this.logger = new Logger('DeepgramService.ts', true)
  }

  /**
   * Creates a connection to the Deepgram service.
   * @param {number} sampleRate - The sample rate of the audio.
   * @returns {Promise<any>} A promise that resolves to the Deepgram connection.
   */
  async createConnection(sampleRate: number = 44100, keywords: string[]) {
    const deepgram = createClient(_DEEPGRAM_API_KEY_)
    return await deepgram.listen.live({
      model: 'nova-2',
      punctuate: false,
      language: 'en-GB',
      encoding: 'linear16',
      sample_rate: sampleRate,
      interim_results: true,
      keywords: keywords
    })
  }

  // questionId is closure-captured so late transcripts route after the active question moves on.
  setupConnectionListeners(
    connection: any,
    questionId: number,
    routeTranscript: (questionId: number, result: TranscriptionResult) => void,
    findMatches: (text: string) => TranscriptionResult,
    onError?: (error: unknown) => void
  ): DeepgramConnectionHandle {
    const handle: DeepgramConnectionHandle = { connection, questionId, disposed: false }

    connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
      if (handle.disposed) return
      const text = data.channel.alternatives[0].transcript
      if (text) {
        const result = findMatches(text)
        routeTranscript(questionId, result)
      }
    })

    connection.on(LiveTranscriptionEvents.Error, (error: any) => {
      if (handle.disposed) return
      this.logger.error('Deepgram error:', error)
      if (onError) {
        onError(error)
      }
    })

    return handle
  }

  closeHandle(handle: DeepgramConnectionHandle | null) {
    if (!handle) return
    handle.disposed = true
    if (handle.connection) {
      handle.connection.finish()
    }
  }

  /**
   * Sends audio data to the Deepgram service.
   * @param {any} connection - The Deepgram connection.
   * @param {Float32Array} audioData - The audio data to send.
   */
  sendAudioData(connection: any, audioData: Float32Array) {
    const int16Array = new Int16Array(audioData.map((x) => x * 32767))
    const uint8Array = new Uint8Array(int16Array.buffer)
    if (connection) {
      connection.send(uint8Array)
    }
  }

  /**
   * Checks the connection to the Deepgram service.
   * @returns {Promise<boolean>} A promise that resolves to true if the connection is successful, false otherwise.
   */
  checkConnection(): Promise<boolean> {
    return new Promise((resolve) => {
      let connection: any = null

      const timeout = setTimeout(() => {
        this.logger.error('Deepgram connection check timed out.')
        if (connection) {
          this.stopConnection(connection)
        }
        resolve(false)
      }, 5000) // 5 second timeout

      try {
        const deepgram = createClient(_DEEPGRAM_API_KEY_)
        connection = deepgram.listen.live({
          model: 'nova-2',
          punctuate: false,
          language: 'en-GB',
          encoding: 'linear16',
          sample_rate: 44100,
          interim_results: true,
          keywords: []
        })

        connection.on(LiveTranscriptionEvents.Open, () => {
          clearTimeout(timeout)
          this.stopConnection(connection)
          resolve(true)
        })

        connection.on(LiveTranscriptionEvents.Error, (error: any) => {
          clearTimeout(timeout)
          this.logger.error('Deepgram connection check failed with error:', error)
          this.stopConnection(connection)
          resolve(false)
        })

        connection.on(LiveTranscriptionEvents.Close, () => {
          clearTimeout(timeout)
          const dataStore = useDataStore()
          if (dataStore.screenerInProgress) {
            this.logger.error('Deepgram connection check failed, connection closed.')
          }
          resolve(false)
        })
      } catch (error) {
        clearTimeout(timeout)
        this.logger.error('Deepgram connection check failed:', error)
        resolve(false)
      }
    })
  }

  /**
   * Stops the Deepgram connection.
   * @param {any} connection - The Deepgram connection to stop.
   */
  stopConnection(connection: any) {
    if (connection) {
      connection.finish()
    }
  }
}
